/* eslint-disable */
/**
 * useFirebaseData
 *
 * Central hook that replaces every localStorage + window.dispatchEvent pattern
 * in the app with Firebase Realtime Database listeners.
 *
 * Returns:
 *   weeklyMeals        — live-synced object { Sunday: ..., Monday: ..., ... }
 *   persistMeals(obj)  — write weeklyMeals to DB
 *   recipes            — array of all recipes for this user
 *   ingredients        — array of all ingredients for this user
 *   storeItems         — array of storeItem categories for this user
 *   saveRecipe(recipe) — upsert a recipe by recipe.id
 *   deleteRecipe(id)   — delete a recipe by id
 *   saveIngredient(ing)— upsert an ingredient
 *   deleteIngredient(name) — delete an ingredient by name key
 *   saveStoreItems(arr)— overwrite storeItems array
 *   loading            — true while initial data is being fetched
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { ref, onValue, set, remove } from 'firebase/database';
import { db } from '../firebase/firebase';
import { useAuth } from './AuthContext';

const EMPTY_WEEK = {
  Sunday: null, Monday: null, Tuesday: null,
  Wednesday: null, Thursday: null, Friday: null, Saturday: null,
};

// Only update state if the data actually changed — prevents unnecessary
// re-renders (and image re-fetches) when Firebase fires with identical data
function useStableState(initial) {
  const [state, setState] = useState(initial);
  const jsonRef = useRef(JSON.stringify(initial));

  const setStable = useCallback((newVal) => {
    const newJson = JSON.stringify(newVal);
    if (newJson !== jsonRef.current) {
      jsonRef.current = newJson;
      setState(newVal);
    }
  }, []);

  return [state, setStable];
}

export function useFirebaseData() {
  const { user } = useAuth();
  const uid = user?.uid;

  const [weeklyMeals, setWeeklyMeals] = useStableState(EMPTY_WEEK);
  const [recipes,     setRecipes]     = useStableState([]);
  const [ingredients, setIngredients] = useStableState([]);
  const [storeItems,  setStoreItems]  = useStableState([]);
  const [loading,     setLoading]     = useState(true);

  const userRef = (path) => ref(db, `users/${uid}/${path}`);

  useEffect(() => {
    if (!uid) { setLoading(false); return; }

    let resolved = 0;
    const done = () => { resolved++; if (resolved === 4) setLoading(false); };

    const unsubMeals = onValue(userRef('weeklyMeals'), (snap) => {
      setWeeklyMeals(snap.exists() ? { ...EMPTY_WEEK, ...snap.val() } : EMPTY_WEEK);
      done();
    });

    const unsubRecipes = onValue(userRef('recipes'), (snap) => {
      setRecipes(snap.exists() ? Object.values(snap.val()) : []);
      done();
    });

    const unsubIngredients = onValue(userRef('ingredients'), (snap) => {
      setIngredients(snap.exists() ? Object.values(snap.val()) : []);
      done();
    });

    const unsubStoreItems = onValue(userRef('storeItems'), (snap) => {
      setStoreItems(snap.exists() ? snap.val() : []);
      done();
    });

    return () => {
      unsubMeals();
      unsubRecipes();
      unsubIngredients();
      unsubStoreItems();
    };
  }, [uid]);

  const persistMeals = useCallback(async (updated) => {
    if (!uid) return;
    setWeeklyMeals(updated);
    await set(userRef('weeklyMeals'), updated);
  }, [uid]);

  const saveRecipe = useCallback(async (recipe) => {
    if (!uid || !recipe.id) return;
    await set(userRef(`recipes/${String(recipe.id)}`), recipe);
  }, [uid]);

  const deleteRecipe = useCallback(async (id) => {
    if (!uid) return;
    await remove(userRef(`recipes/${String(id)}`));
  }, [uid]);

  const ingredientKey = (name) =>
    String(name).toLowerCase().replace(/[^a-z0-9]/g, '_');

  const saveIngredient = useCallback(async (ingredient) => {
    if (!uid || !ingredient.name) return;
    await set(userRef(`ingredients/${ingredientKey(ingredient.name)}`), ingredient);
  }, [uid]);

  const deleteIngredient = useCallback(async (name) => {
    if (!uid) return;
    await remove(userRef(`ingredients/${ingredientKey(name)}`));
  }, [uid]);

  const saveStoreItems = useCallback(async (arr) => {
    if (!uid) return;
    await set(userRef('storeItems'), arr);
  }, [uid]);

  return {
    weeklyMeals, persistMeals,
    recipes,     saveRecipe,   deleteRecipe,
    ingredients, saveIngredient, deleteIngredient,
    storeItems,  saveStoreItems,
    loading,
  };
}